import { Square } from '@mui/icons-material';
import { Box, Button, FormControlLabel, FormGroup, Link } from '@mui/material';
import {
  DataGridPro,
  GridFooterContainer,
  GridRowCount,
  GridSelectedRowCount,
  GridValidRowModel,
  useGridApiContext,
} from '@mui/x-data-grid-pro';
import { DataGridProProps } from '@mui/x-data-grid-pro/models/dataGridProProps';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { AbsenceType } from '~/data/absences/types';
import { AbsenceHistoryGridTreeDataGroupingCell } from '~/scenes/PeopleModule/components/Tabs/AbsencesTab/components/AbsencesTable/components/AbsenceHistoryDialog/AbsenceHistoryDialogContent/AbsenceHistoryGridTreeDataGroupingCell';
import { useAbsenceHistoryData } from '~/scenes/PeopleModule/components/Tabs/AbsencesTab/components/AbsencesTable/components/AbsenceHistoryDialog/AbsenceHistoryDialogContent/useAbsenceHistoryData';
import { useAbsenceHistoryTable } from '~/scenes/PeopleModule/components/Tabs/AbsencesTab/components/AbsencesTable/components/AbsenceHistoryDialog/AbsenceHistoryDialogContent/useAbsenceHistoryTable';
import { AbsenceStatusCell } from '~/scenes/PeopleModule/components/Tabs/AbsencesTab/components/AbsencesTable/components/AbsenceStatusCell';
import { AbsenceHistoryTableRow } from '~/scenes/PeopleModule/components/Tabs/AbsencesTab/components/AbsencesTable/data';
import { useAbsenceTableUtils } from '~/scenes/PeopleModule/components/Tabs/AbsencesTab/components/AbsencesTable/useAbsenceTableUtils';
import { LoadingView } from '~/scenes/PortalViews/components/LoadingView';
import { getFormattedDateString } from '~/utils/helpers/date';

import { useSxStyles } from './useStyles';

type PropsType = {
  employeeId: number;
  absenceType: AbsenceType;
};

type CustomAbsenceHistoryTableFooterProps = {
  isShowMoreButtonVisible: boolean;
  historical: {
    isLoading: boolean;
    showHistorical: boolean;
    setShowHistorical: (show: boolean) => void;
  };
};

const CustomAbsenceHistoryTableFooter = (
  props: CustomAbsenceHistoryTableFooterProps,
) => {
  const gridApi = useGridApiContext();

  const visibleRowIds = gridApi.current.getSortedRowIds();

  const visibleRowModels = visibleRowIds.map((id) =>
    gridApi.current.getRow(id),
  );

  const rowCount = visibleRowModels.length;
  const selectedRowsCount = gridApi.current.getSelectedRows().size;

  const { isShowMoreButtonVisible, historical } = props;

  const { t } = useTranslation();

  return (
    <GridFooterContainer
      sx={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr' }}
    >
      <Box>
        {!!selectedRowsCount && (
          <GridSelectedRowCount selectedRowCount={selectedRowsCount} />
        )}
      </Box>

      <Box>
        {isShowMoreButtonVisible && (
          <Button onClick={() => historical.setShowHistorical(true)}>
            {t('laMoreHistory')}
          </Button>
        )}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        {!!rowCount && (
          <GridRowCount rowCount={rowCount} visibleRowCount={rowCount} />
        )}
      </Box>
    </GridFooterContainer>
  );
};

export const AbsenceHistoryDialogContent = <V extends GridValidRowModel, F>(
  props: PropsType,
) => {
  const { employeeId, absenceType } = props;

  const sxStyles = useSxStyles();
  const { t } = useTranslation();

  const { isLoading, absencesHistory, getHistoryRowType, historical } =
    useAbsenceHistoryData({
      employeeId,
      absenceType,
    });

  const { rows, columnConfigs } = useAbsenceHistoryTable({
    absencesHistory,
    absenceType,
  });

  const { filterAvailableColumns, getColumnsWithCellRendering } =
    useAbsenceTableUtils<AbsenceHistoryTableRow, V, F>({
      employeeId,
      absenceType,
      canApproveAbsence: false,
      canEditAbsences: false,
    });

  const { columnsConfigWidthRenders } = useMemo(() => {
    const columnsConfigWidthRenders = getColumnsWithCellRendering({
      columnConfigs: filterAvailableColumns(columnConfigs),
      renderStatusComponent: (componentProps, row) => (
        <AbsenceStatusCell
          {...componentProps}
          canApproveAbsence={true}
          isHistory={row.hierarchy.length > 1}
        />
      ),
      renderAttachmentComponent: (componentProps) => (
        <Link {...componentProps} />
      ),
      renderCreatedComponent: (componentProps) => {
        const { created, name } = componentProps;

        const getTimeEntry = (
          name?: string,
          time?: Date,
        ): string | undefined => {
          return !!name && !!time
            ? `${getFormattedDateString(time, 'L')} - ${name}`
            : undefined;
        };

        return getTimeEntry(name, created);
      },
    });

    return { columnsConfigWidthRenders };
  }, [columnConfigs, filterAvailableColumns, getColumnsWithCellRendering]);

  const groupingColDef: DataGridProProps['groupingColDef'] = {
    headerName: '',
    width: 50,
    renderCell: (params) => (
      <AbsenceHistoryGridTreeDataGroupingCell {...params} />
    ),
  };

  const isShowMoreButtonVisible =
    !historical.showHistorical || historical.isLoading;

  if (isLoading) {
    return <LoadingView />;
  }

  return (
    <Box sx={sxStyles.root}>
      <FormGroup row={true} sx={sxStyles.legend}>
        <FormControlLabel
          control={<Square sx={sxStyles.legendComing} />}
          label={t('laComing')}
        />
        <FormControlLabel
          control={<Square sx={sxStyles.legendOngoing} />}
          label={t('laOngoing')}
        />
        <FormControlLabel
          control={<Square sx={sxStyles.legendPast} />}
          label={t('laPast')}
        />
        <FormControlLabel
          control={<Square sx={sxStyles.legendDelete} />}
          label={t('laDeleted')}
        />
      </FormGroup>
      <Box>
        <DataGridPro
          sx={sxStyles.dataGrid}
          treeData={true}
          columns={columnsConfigWidthRenders}
          rows={rows}
          density={'compact'}
          getTreeDataPath={(row) => row.hierarchy}
          groupingColDef={groupingColDef}
          getRowClassName={(params) => {
            const {
              row: { hierarchy, meta },
            } = params;

            if (hierarchy.length > 1) {
              return '';
            }

            return `absence-${getHistoryRowType(meta)}`;
          }}
          slots={{
            footer: () => (
              <CustomAbsenceHistoryTableFooter
                isShowMoreButtonVisible={isShowMoreButtonVisible}
                historical={historical}
              />
            ),
          }}
        />
      </Box>
    </Box>
  );
};
